import { useLang } from '../i18n/lang';

/*
 * WHAT IS ON THE MAP, AND WHAT THE READER HAS PUT AWAY.
 *
 * A row along the bottom: one control per scripture and one per kind of thing. Tapping one hides
 * every name it covers; tapping it again brings them back. Nothing opens, nothing closes, and the
 * state of the map is legible without touching anything — which is the whole argument for a row
 * over the accordion this replaces.
 *
 * WHY IT IS A ROW AND NOT A PANEL. `MapLegend` says what the colours mean and hides itself behind
 * a summary, so learning that gold means a divine name costs a click, and HIDING the divine names
 * costs three: open, toggle, close. The reference puts the same nine controls along the bottom edge
 * where they are always readable and always one tap from acting, and this project's own principle
 * is fewest clicks and least visible interface. That is the case for copying it, and it is the only
 * reason to.
 *
 * IT DRIVES A FILTER THAT WAS ALREADY THERE. `map/v8/web.ts` has read `c_<corpus>` and `k_<kind>`
 * since it was ported and differentially tested, and until this existed nothing could set one —
 * `V8Map` passed a literal `{}`. The keys are the engine's own, deliberately: a friendlier spelling
 * here would be a second answer to "is this name shown".
 */

/** One control. `key` is the engine's own — `c_HB`, `k_person`. */
export interface FilterChip {
  key: string;
  label: string;
  /** The colour its dot wears. Kinds share one ink; scriptures each have their own. */
  colour: string;
  /** How the mark is drawn, so a reader can tell a place from a person without reading. */
  shape: 'disc' | 'square' | 'diamond' | 'ring';
}

export interface MapFilterBarProps {
  chips: readonly FilterChip[];
  /** True for hidden, keyed as the engine keys them. */
  off: Readonly<Record<string, boolean | undefined>>;
  onToggle: (key: string) => void;
}

/** The mark beside a label — a shape as well as a colour, because colour alone excludes readers. */
function mark(chip: FilterChip): React.JSX.Element {
  const base = { backgroundColor: chip.shape === 'ring' ? 'transparent' : chip.colour };
  const style
    = chip.shape === 'square' ? { ...base, borderRadius: '2px' }
      : chip.shape === 'diamond' ? { ...base, borderRadius: '1px', transform: 'rotate(45deg)' }
        : chip.shape === 'ring' ? { ...base, border: `1.5px solid ${chip.colour}`, borderRadius: '50%' }
          : { ...base, borderRadius: '50%' };
  return <span className="filter-mark" style={style} aria-hidden="true" />;
}

export function MapFilterBar({ chips, off, onToggle }: MapFilterBarProps): React.JSX.Element {
  const { t } = useLang();
  return (
    <div className="filter-bar" data-map-chrome role="group" aria-label={t('legend.open')}>
      {chips.map(chip => {
        const hidden = off[chip.key] === true;
        return (
          <button
            key={chip.key}
            type="button"
            className={hidden ? 'filter-chip filter-chip-off' : 'filter-chip'}
            /*
             * `aria-pressed` says SHOWN rather than hidden, because the button's label is the thing
             * itself — "Hebrew Bible" pressed reads as "Hebrew Bible is on", which is what a reader
             * of a screen reader needs. Naming it after the hiding would invert every announcement.
             */
            aria-pressed={!hidden}
            onClick={() => { onToggle(chip.key); }}
          >
            {mark(chip)}
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}
